import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

/** Mirrors ClientLogEntry on the backend. Field names must match exactly. */
export interface ClientLogEntry {
  kind: 'Error' | 'Toast';
  message: string;
  stack?: string;
  url?: string;
  traceId?: string;
  appVersion?: string;
  occurredAt?: string;
}

/**
 * Ships browser errors and error toasts to the backend so they land in Loki
 * beside the server-side logs for the same session.
 *
 * Three properties matter more than completeness here. It must never throw, never
 * retry into a storm, and never report its own failures. A telemetry pipeline
 * that takes down the app it is observing is worse than no telemetry.
 *
 * Correlation is free: this posts through HttpClient, so traceInterceptor stamps
 * it with the same X-Session-Id as every other request, and the report lands in
 * the user's existing timeline. Nothing here deals with identity.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
  /** Matches the backend's batch cap. A larger batch would be rejected wholesale. */
  private static readonly MAX_BUFFER = 20;
  private static readonly MAX_MESSAGE_LENGTH = 500;
  private static readonly MAX_STACK_LENGTH = 4000;
  private static readonly FLUSH_DELAY_MS = 2000;

  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.apiBaseUrl}/api/client-logs`;

  private buffer: ClientLogEntry[] = [];
  private flushHandle: ReturnType<typeof setTimeout> | null = null;

  /** An unhandled exception — the screen broke. */
  report(error: unknown, context?: string): void {
    this.enqueue({
      kind: 'Error',
      message: this.compose(this.extractMessage(error), context),
      stack: this.truncate(error instanceof Error ? error.stack : undefined,
                           TelemetryService.MAX_STACK_LENGTH),
    });
  }

  /** An error toast the user was actually shown. */
  reportToast(message: string): void {
    this.enqueue({ kind: 'Toast', message });
  }

  flush(): void {
    if (this.flushHandle !== null) {
      clearTimeout(this.flushHandle);
      this.flushHandle = null;
    }

    if (this.buffer.length === 0) {
      return;
    }

    const entries = this.buffer;
    this.buffer = [];

    // Fire and forget. A dropped batch is an acceptable loss; a retry queue that
    // grows while the backend is unreachable is not — that is how telemetry turns
    // a backend outage into a browser one.
    this.http.post(this.endpoint, { entries }).subscribe({
      next: () => undefined,
      error: () => undefined,
    });
  }

  private enqueue(entry: ClientLogEntry): void {
    // Self-report guard. Without it, one unreachable backend becomes an unbounded
    // loop of failed reports about failed reports.
    if (entry.message.includes('/api/client-logs')) {
      return;
    }

    // A render loop can call this thousands of times a second. Dropping past the
    // cap bounds memory; the backend's rate limiter bounds the rest.
    if (this.buffer.length >= TelemetryService.MAX_BUFFER) {
      return;
    }

    this.buffer.push({
      ...entry,
      message: this.truncate(entry.message, TelemetryService.MAX_MESSAGE_LENGTH)!,
      url: this.safeUrl(),
      appVersion: environment.production ? 'production' : 'development',
      occurredAt: new Date().toISOString(),
    });

    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushHandle !== null) {
      return;
    }

    this.flushHandle = setTimeout(() => {
      this.flushHandle = null;
      this.flush();
    }, TelemetryService.FLUSH_DELAY_MS);
  }

  private compose(message: string, context?: string): string {
    return context ? `[${context}] ${message}` : message;
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;

    try {
      return JSON.stringify(error) ?? 'Unknown error';
    } catch {
      return 'Unserializable error';
    }
  }

  /** The SPA route the user was on, not the API path. */
  private safeUrl(): string | undefined {
    try {
      return window.location.pathname + window.location.search;
    } catch {
      return undefined;
    }
  }

  private truncate(value: string | undefined, max: number): string | undefined {
    if (value === undefined) return undefined;
    return value.length > max ? value.slice(0, max) : value;
  }
}
